import type { JSX } from 'react'
import type { GospelMonth } from '../api'
import { isNegative, monthLabel, yen } from '../format'

// The GOSPEL masthead, in the shape leadership already reads it: months across
// the top, the five rows down the side. Deliberately the sheet's layout — the
// point of this page is that the same table is now computed rather than
// hand-consolidated, and a different arrangement would obscure that.

const ROWS = [
  { key: 'actualAr', label: 'Actual AR', sheetRow: 3 },
  { key: 'actualAp', label: 'Actual AP', sheetRow: 4 },
  { key: 'forecastNetNewAr', label: 'Net new AR', sheetRow: 5 },
  { key: 'forecastNetNewAp', label: 'Net new AP', sheetRow: 6 },
  { key: 'netCashDifference', label: 'Net cash', sheetRow: 7 },
] as const

export function Masthead({ months }: { months: GospelMonth[] }): JSX.Element {
  if (months.length === 0) {
    return (
      <section className="panel">
        <h2>Cash flow</h2>
        <p className="notice">No months in range.</p>
      </section>
    )
  }

  const anyFundraise = months.some((month) => Number(month.actualFundraise) !== 0)

  return (
    <section className="panel">
      <h2>Cash flow</h2>
      <div className="scroller">
        <table>
          <thead>
            <tr>
              <th scope="col" className="rowhead">
                Row
              </th>
              {months.map((month) => (
                <th key={month.month} scope="col">
                  {monthLabel(month.month)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.key}>
                <th scope="row" className="rowhead">
                  {row.label} <span className="sheetref">r{row.sheetRow}</span>
                </th>
                {months.map((month) => {
                  // The AP row is special. With actuals it shows them; a month
                  // with no actuals yet shows the headcount plan's baseline
                  // (planPayroll), marked as a forecast, not dragged sideways
                  // from the prior month (what the parity harness calls
                  // 'extrapolated'); a month with neither shows 'no data'.
                  if (row.key === 'actualAp' && month.apBasis === 'plan') {
                    return (
                      <td
                        key={month.month}
                        className="negative planned"
                        title="Planned payroll from the headcount plan (no actuals yet)"
                      >
                        {yen(month.planPayroll)}
                      </td>
                    )
                  }
                  const unknown = row.key === 'actualAp' && month.apBasis !== 'actual'
                  const value = month[row.key]
                  return (
                    <td
                      key={month.month}
                      className={unknown ? 'unknown' : isNegative(value) ? 'negative' : ''}
                      title={unknown ? 'No payroll actuals for this month' : undefined}
                    >
                      {unknown ? 'no data' : yen(value)}
                    </td>
                  )
                })}
              </tr>
            ))}
            {anyFundraise && (
              <tr className="secondary">
                <th scope="row" className="rowhead">
                  of which fundraise
                </th>
                {months.map((month) => (
                  <td key={month.month}>
                    {Number(month.actualFundraise) === 0 ? '—' : yen(month.actualFundraise)}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}
